import { apiConnection } from "@/services/api-connection";
import { messengerMessageManager } from "@/services/messenger-message/messenger-message-manager";
import { i18n } from "@/i18n";
import { fileManager } from "@/services/file/file-manager";
import { notificationManager } from "@/services/utilities/notification-manager";
import { accessManager } from "@/services/security/access-manager";
import { studeaManagerManager } from "@/services/studea-manager/studea-manager-manager";

const URL_LIST = "/app/triad/list/";
const URL_ONE = "/app/triad/";
const URL_PDF = "/app/triad/pdf/";
const URL_CONTRACT_RUPTURE_FILE = "/app/contract/rupture/file/download/";

class TriadManager {
  APPRENTICE_PROFILE = 1;
  TUTOR_PROFILE = 2;
  APPRENTICE_MASTER_PROFILE = 3;
  STUDEA_MANAGER_PROFILE = 4;
  STUDEA_ADMIN_PROFILE = 5;
  STUDEA_SEND_REPORT_IN_PREPARATION = 0;
  STUDEA_SEND_REPORT_SENT = 1;
  STUDEA_SEND_REPORT_ERROR = 2;
  KEEP_DUPLICATE_USER = 1;
  DELETE_DUPLICATE_USER = 0;

  loadTriadList = (id, archive, search = "") => {
    return apiConnection.get(URL_LIST + id, { archive: archive, search: search }, true).then((response) => response);
  };
  loadOne = (id) => {
    return apiConnection.get(URL_ONE + id, {}, true).then((response) => response);
  };

  pdf(id, keyDates = false, zip = false) {
    return apiConnection.get(URL_PDF + id, { keyDates: keyDates ? 1 : 0, zip: zip ? 1 : 0 }).then((res) => {
      messengerMessageManager.processMessage(res);
    });
  }

  downlaodContractRuptureFile(
    id,
    data,
    fileManagerService = fileManager,
    notificationManagerService = notificationManager,
  ) {
    return apiConnection.post(URL_CONTRACT_RUPTURE_FILE + id, data).then((res) => {
      if (res.downloadUrl) {
        const apiPath = fileManagerService.getApiPath().slice(0, -4) + res.downloadUrl;
        const link = document.createElement("a");
        link.setAttribute("href", apiPath);
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        notificationManagerService.showNotification("success", i18n.global.t("download_success"));
      }
    });
  }

  userCanSeeQuestionnaireMark(profile, questionnaire) {
    return (
      profile === this.STUDEA_MANAGER_PROFILE ||
      profile === this.STUDEA_ADMIN_PROFILE ||
      !!(
        questionnaire &&
        questionnaire.marksVisibleBy &&
        Array.isArray(questionnaire.marksVisibleBy) &&
        questionnaire.marksVisibleBy.includes(profile)
      )
    );
  }

  setProfileLabel(
    key,
    apprenticeProfileLabel = {},
    tutorProfileLabel = {},
    apprenticeMasterProfileLabel = {},
    studeaManagerProfileLabel = {},
  ) {
    switch (key) {
      case this.APPRENTICE_PROFILE:
        return apprenticeProfileLabel?.title ? apprenticeProfileLabel.title : i18n.global.t("apprentice");
      case this.TUTOR_PROFILE:
        return tutorProfileLabel?.title ? tutorProfileLabel.title : i18n.global.t("pedagogic_tutor");
      case this.APPRENTICE_MASTER_PROFILE:
        return apprenticeMasterProfileLabel?.title
          ? apprenticeMasterProfileLabel.title
          : i18n.global.t("apprenticeMaster");
      case this.STUDEA_MANAGER_PROFILE:
        return studeaManagerProfileLabel?.title ? studeaManagerProfileLabel.title : i18n.global.t("studeaManager");
    }
  }

  getContractRuptureDateForm(contractId) {
    return apiConnection.get("/app/contract/rupture/form/" + contractId).then((res) => res);
  }

  postContractRuptureDateForm(contractId, data) {
    return apiConnection.post("/app/contract/rupture/post/" + contractId, data);
  }

  validationContractRuptureJustification(contractId, invalid = false) {
    return apiConnection.get("/app/contract/rupture/" + (invalid ? "in" : "") + "validate-justification/" + contractId);
  }

  getTriadInfoTitle(item) {
    switch (item.type) {
      case "presentation":
        return item.label ? item.label : i18n.global.t("presentation");
      case "contact":
        return item.label ? item.label : i18n.global.t("contact");
      case "partner":
        return item.label ? item.label : i18n.global.t("partner");
      case "communication":
        return item.label ? item.label : i18n.global.t("communication");
    }
  }

  getTriadInfoIcon(type) {
    switch (type) {
      case "presentation":
        return "presentation-screen";
      case "contact":
        return "id-card-clip";
      case "partner":
        return "handshake";
      case "communication":
        return "megaphone";
    }
  }

  async cancelContractRupture(contractId) {
    return await apiConnection.put("/app/contract/rupture/put/reinit/" + contractId);
  }

  displayRuptureButton(contract, trainingCourse, triad, user) {
    const access = accessManager.ALL;
    const code = studeaManagerManager.STUDEA_MANAGER_TRIAD_CONTRACT_RUPTURE_VALIDATOR;

    if (user?.isAdmin || accessManager.isAvailableForUser(null, code, access)) {
      return (
        contract.activeContract &&
        !!contract.ruptureDate?.length &&
        !contract.ruptureJustificationValidated &&
        trainingCourse?.useTriadDeclareContractRupture
      );
    }

    const roles = triad?.userRoles || [];
    return (
      contract.activeContract &&
      (roles.includes(triadManager.APPRENTICE_PROFILE) || roles.includes(triadManager.APPRENTICE_MASTER_PROFILE)) &&
      !contract.ruptureJustificationValidated &&
      trainingCourse?.useTriadDeclareContractRupture
    );
  }
}

export const triadManager = new TriadManager();
